import {
  collectRoutines,
  dispatchAction,
  extractPayload,
  ofType,
  routine,
  withNamespace,
} from '@ardoq/rxbeach';
import { debounceTime, tap, withLatestFrom } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { prototypeInterface } from 'modelInterface/prototype/prototypeInterface';
import {
  applyFiltersForContextSwitcher,
  openComponents,
  removeUnselectedLoadedComponents,
  setComponentName,
  setComponentTypeName,
  setHasSearchResults,
  setIsLoading,
  setLoadedComponents,
} from './actions';
import { loadedStateOperations } from 'loadedState/loadedStateOperations';
import { isArdoqError } from '@ardoq/common-helpers';
import { buildTraversalState, isError } from 'loadedState/buildState';
import { requestShowAppModule } from 'appContainer/actions';
import { AppModules } from 'appContainer/types';
import { registerLoadedState } from 'loadedState/actions';
import { logError } from '@ardoq/logging';
import { featureOperations, Features } from '@ardoq/features';
import { setActiveMainTabLeft } from 'streams/views/mainContent/actions';
import {
  isComponent,
  isQueryBuilderRule,
  QueryBuilderQuery,
  QueryBuilderRule,
  QueryBuilderSubquery,
  ViewIds,
  ComponentSelection,
  GroupType,
} from '@ardoq/api-types';
import { currentViewId$ } from 'streams/currentViewId$';
import {
  getSupportedTraversalViewIdOrDefault,
  toRenderedViewpointViewId,
} from 'views/environmentals/getViewpointModeSupportedViews';
import { viewIdToUserFriendlyName } from 'views/metaInfoConsts';
import { isViewpointMode$ } from 'traversals/loadedGraph$';
import {
  ComponentSearchData,
  ComponentSearchState,
  ComponentSearchViewState,
} from './types';
import { buildSearchData } from './buildSearchData';
import { getRepresentationDataWithColorVariantsForComponent } from '../components/getRepresentationDataWithColorVariants';
import { componentInterface } from '@ardoq/component-interface';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { enterViewpointMode } from 'streams/traversals/actions';
import { resetAllSearchState } from '../../search/actions';
import { scopeDataLoaded } from '../../traversals/stagedLoadedDataAndState$';
import {
  setIsLoadingWithConfig,
  setIsLoading as setLOaderIsLoading,
} from 'components/GlobalPaneLoader/globalPaneLoader$';
import { featureContext$ } from 'streams/featureContext$';
import { isDiscoverJourney } from 'canvas/isDiscoverJourney';
import { setApplicationViewState } from 'canvas/contextToolBar/loadedStateHistory/setApplicationViewState';
import { getDefaultWildcardViewpoint } from 'traverseOneLevel/defaultWildcardViewpoint';
import { anyType } from '@ardoq/global-consts';
import { maybeConvertWildcardStartType } from 'loadedState/maybeConvertWildcardStartType';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t } from '@ardoq/i18n';

export const getHandleTraversalSearchRoutine = (
  componentSearch$: Observable<ComponentSearchViewState>,
  namespace: string
) =>
  routine(
    withNamespace(namespace),
    ofType(
      setComponentName,
      setComponentTypeName,
      resetAllSearchState,
      applyFiltersForContextSwitcher
    ),
    debounceTime(300),
    withLatestFrom(componentSearch$),
    tap(async ([, componentSearchState]) => {
      if (
        componentSearchState.componentName === '' &&
        componentSearchState.componentType === ''
      ) {
        // when user clears inputs, we want to keep displaying only selected components
        dispatchAction(removeUnselectedLoadedComponents(), namespace);
        dispatchAction(setHasSearchResults(false), namespace);
        return;
      }
      dispatchAction(setIsLoading(true), namespace);
      const result = await loadComponents(componentSearchState);
      if (isArdoqError(result)) {
        logError(result);
        dispatchAction(setIsLoading(false), namespace);
        return;
      }
      dispatchAction(setLoadedComponents(result), namespace);
      dispatchAction(setIsLoading(false), namespace);
      dispatchAction(setHasSearchResults(true), namespace);
    })
  );
const loadComponents = async ({
  componentName,
  componentType,
  startSetFilter,
}: ComponentSearchState) => {
  const result = await prototypeInterface.loadComponentsFromAdvancedSearch(
    componentName,
    componentType,
    startSetFilter
  );
  if (isArdoqError(result)) {
    return result;
  }
  const { results: loadedComponents, totalCount } = result;

  return {
    loadedComponents,
    totalCount,
  };
};

const extractRules = (query: QueryBuilderSubquery): QueryBuilderRule[] =>
  query.rules
    .flatMap(ruleOrQuery =>
      isQueryBuilderRule(ruleOrQuery) ? ruleOrQuery : extractRules(ruleOrQuery)
    )
    .filter(isQueryBuilderRule);

export const toComponentSearchDataById = (
  id: string
): ComponentSearchData | null => {
  const component = componentInterface.get(id);
  if (!isComponent(component)) return null;
  return buildSearchData(
    component,
    getRepresentationDataWithColorVariantsForComponent(
      component,
      componentInterface.resolveComponentType(component)
    ),
    workspaceInterface.getWorkspaceName(component.rootWorkspace) ?? ''
  );
};

// This is a temporary solution to get the start type and search term from the query
// of the block to be edited
// TODO CL this needs fixing when AdvancedSearch is added to the viewpoint builder
export const getStartTypeAndTermFromQuery = (query: QueryBuilderQuery) => {
  const queryRules = extractRules(query);
  const startType = queryRules.find(rule => rule.field === 'typeName');
  const startSearchTerm = queryRules.find(
    rule => rule.field === 'name' && rule.operator === 'contains_substring'
  );
  return {
    type: startType?.value?.toString(),
    term: startSearchTerm?.value?.toString(),
  };
};

const graphViews = new Set([
  ViewIds.MODERNIZED_BLOCK_DIAGRAM,
  ViewIds.DEPENDENCY_MAP_2,
  ViewIds.RELATIONSHIPS_3,
  ViewIds.BLOCKS,
]);

const openSelectedComponents = routine(
  ofType(openComponents),
  extractPayload(),
  withLatestFrom(currentViewId$, isViewpointMode$, featureContext$),
  tap(
    async ([
      { componentSelection, viewId: payloadViewId, withOneStepTraversal },
      currentViewId,
      { isViewpointMode },
      featureContext,
    ]) => {
      // i18nLocale$.state OK here: loader title set once on dispatch; the loader UI is closed before locale could meaningfully change
      const i18nLocale = i18nLocale$.state;
      dispatchAction(
        setIsLoadingWithConfig({
          title: t(
            i18nLocale,
            'viewpointBuilder:loading.components',
            'Loading components...'
          ),
        })
      );

      const activeTabId = getSupportedTraversalViewIdOrDefault(
        payloadViewId
          ? payloadViewId // payloadViewId is used when opening components directly from the inventory to get to a specific view
          : isViewpointMode
            ? // to prevent changing the View when adding datasets
              currentViewId
            : undefined
      );

      const { params: initialParams, promise } = getParamsAndRequestPromise(
        componentSelection,
        activeTabId,
        withOneStepTraversal
      );
      const loadedGraphOrError = await promise;

      if (isError(loadedGraphOrError)) {
        logError(
          new Error('Failed to open components'),
          loadedGraphOrError.error
        );

        dispatchAction(setLOaderIsLoading(false));
        return;
      }

      const params = maybeConvertWildcardStartType(
        initialParams,
        loadedGraphOrError
      );

      dispatchAction(
        scopeDataLoaded({
          trackingLocation: 'scopifiedSearch',
          scopeData: loadedGraphOrError.scopeData,
        })
      );

      dispatchAction(enterViewpointMode());

      dispatchAction(
        requestShowAppModule({ selectedModule: AppModules.WORKSPACES })
      );

      const isCanvasPrototype = featureOperations.hasFeature(
        featureContext,
        Features.CANVAS_PROTOTYPE
      );

      // Only create a new history entry if the user is coming form the home
      // page or from the inventory. A new data block created in the view (or
      // editing an existing one) will be handled by an iterations of the
      // loaded state by pushing the newest loaded state to the history.
      // Checking isViewpointMode is indirect, but it should be fine, we are
      // clearing all view state before starting any of these journeys.
      const isSourceInventoryOrHomepage = !isViewpointMode;
      if (
        (isDiscoverJourney() || isCanvasPrototype) &&
        isSourceInventoryOrHomepage
      ) {
        setApplicationViewState({
          conditionalFormatting: [],
          groupBys: [
            {
              type: GroupType.WORKSPACE,
            },
            {
              type: GroupType.PARENT_ALL,
            },
          ],
          loadedStates: [
            loadedStateOperations.attachLoadedGraph(params, loadedGraphOrError),
          ],
          viewNameCandidate: activeTabId,
          viewSettings: null,
        });
      } else {
        dispatchAction(
          setActiveMainTabLeft({
            activeTabId: toRenderedViewpointViewId(activeTabId),
            isViewpointMode: true,
          })
        );
        const loadedStates = [
          loadedStateOperations.attachLoadedGraph(params, loadedGraphOrError),
        ];
        dispatchAction(
          registerLoadedState({
            loadedStates,
            viewName:
              viewIdToUserFriendlyName[toRenderedViewpointViewId(activeTabId)],
          })
        );
      }
      dispatchAction(setLOaderIsLoading(false));
    }
  )
);

const getParamsAndRequestPromise = (
  componentSelection: ComponentSelection,
  activeTabId: ViewIds,
  withOneStepTraversal: boolean = false,
  startType: string = anyType
) => {
  const paths =
    withOneStepTraversal && graphViews.has(activeTabId)
      ? getDefaultWildcardViewpoint(startType)
      : [];
  const traversalParams =
    loadedStateOperations.traversalParamsFromSelectionAndPaths(
      componentSelection,
      paths
    );
  return {
    params: traversalParams,
    promise: buildTraversalState(traversalParams.data),
  };
};

export const componentSearchRoutines = collectRoutines(openSelectedComponents);
